import { useState, useRef } from 'react';
import { Upload, X, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPTED = '.png,.jpg,.jpeg,.webp,.svg';

const ImageUpload = ({ value, onChange, label, testId }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const replaceRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/uploads/card-image`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Upload failed');
      }
      const data = await res.json();
      onChange(data.url);
      // After React re-renders with the new value, focus the Replace button
      // so focus stays inside ImageUpload (not on Exit/Cancel)
      requestAnimationFrame(() => {
        if (replaceRef.current) replaceRef.current.focus();
      });
    } catch (err) { setError(err.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleRemove = () => {
    onChange('');
  };

  const triggerFilePicker = () => {
    fileRef.current?.click();
  };

  const imgSrc = value
    ? (value.startsWith('http') ? value : `${API_URL}${value}`)
    : '';

  return (
    <div data-testid={testId || 'image-upload'}>
      {label && <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{label}</label>}
      {value ? (
        <div className="relative group inline-block">
          <div className="w-32 h-24 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
            <img src={imgSrc} alt="Uploaded" className="w-full h-full object-contain" />
          </div>
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 rounded-lg flex items-center gap-1 justify-center transition-opacity">
            <button ref={replaceRef} type="button" onClick={triggerFilePicker}
              className="p-1.5 bg-white/90 rounded-md text-slate-700 hover:bg-white" title="Replace" data-testid={`${testId}-replace`}>
              <Upload className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={handleRemove}
              className="p-1.5 bg-white/90 rounded-md text-red-500 hover:bg-white" title="Remove" data-testid={`${testId}-remove`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={triggerFilePicker}
          disabled={uploading} data-testid={`${testId}-upload-btn`}
          className="w-32 h-24 rounded-lg border-2 border-dashed border-slate-200 hover:border-[#0077FF]/30 hover:bg-blue-50/30 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50">
          {uploading ? (
            <div className="w-5 h-5 border-2 border-slate-300 border-t-[#0077FF] rounded-full animate-spin" />
          ) : (
            <>
              <Image className="w-5 h-5 text-slate-300" />
              <span className="text-[10px] text-slate-400 font-medium">Upload Image</span>
            </>
          )}
        </button>
      )}
      <input ref={fileRef} type="file" accept={ACCEPTED} onChange={handleUpload}
        className="sr-only" tabIndex={-1} />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default ImageUpload;
